import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { AuthScreen } from './AuthScreen';
import NotAllowed from './NotAllowed';
import SchemaNotExposed from './SchemaNotExposed';
import { useAuth, useMember } from '../../lib/auth';
import { errorMessage, gateFor, useAppGate } from '../../lib/query';

/**
 * Wraps every signed-in screen.
 * - No session → /login.
 * - Session → claim_membership() once, then the `members` row (useMember).
 *   No row → NotAllowed. PGRST106 anywhere → SchemaNotExposed. 42501 on a read → NotAllowed.
 */
export function AuthGate({ children }: { children: ReactNode }) {
  const { session } = useAuth();
  const member = useMember();
  const gate = useAppGate();
  const location = useLocation();

  if (session === undefined) return <Splash />;
  if (!session) return <Navigate to="/login" replace state={{ from: location.pathname + location.search }} />;

  const memberGate = member.error ? gateFor(member.error) : null;
  if (gate === 'schema' || memberGate === 'schema') return <SchemaNotExposed />;
  if (gate === 'forbidden' || memberGate === 'forbidden') return <NotAllowed reason="refused" />;
  if (member.isPending) return <Splash />;
  if (member.error) {
    return (
      <AuthScreen title="Could not check your access">
        <p>{errorMessage(member.error)}</p>
        <button type="button" className="btn primary" onClick={() => void member.refetch()}>
          Try again
        </button>
      </AuthScreen>
    );
  }
  if (!member.data) return <NotAllowed />;
  return <>{children}</>;
}

function Splash() {
  return (
    <div className="auth" aria-busy="true">
      <div className="loading" role="status">
        Loading…
      </div>
    </div>
  );
}
